/**
 * Admin Hub Favorites Component
 * Displays user's favorite admin pages
 */
import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  NavigateNext as NavIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAdminHub } from './context/AdminHubContext';
import { useAdminHubFavorites } from '../../hooks/useAdminHubFavorites';
import { ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

export const AdminHubFavorites: React.FC = () => {
  const { navigateTo } = useAdminHub();
  const { favorites, removeFavorite } = useAdminHubFavorites();

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: 'warning.main', fontSize: 20 }} />
          <Typography variant="h6" fontWeight={500}>
            Favorites
          </Typography>
        </Box>
        {favorites.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {favorites.length} / 8
          </Typography>
        )}
      </Box>

      {/* Favorites list */}
      {favorites.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <StarBorderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2" gutterBottom>
            No favorites yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click the star icon on any admin page to add it here
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {favorites.map((fav) => (
            <ListItem
              key={fav.path}
              disablePadding
              secondaryAction={
                <Tooltip title="Remove from favorites">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(fav.path);
                    }}
                    sx={{
                      opacity: 0.5,
                      '&:hover': {
                        opacity: 1,
                        color: 'warning.main',
                      },
                    }}
                  >
                    <StarIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemButton
                onClick={() => navigateTo(fav.path)}
                sx={{
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <NavIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={fav.label}
                  secondary={fav.section}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default AdminHubFavorites;
